// src/client/components/AppHeader.tsx
// Top application header: hamburger | greeting + name | bell
// Used by the main borrower screens (Apply, Loans, Score, Home, Profile).
import { Menu, Bell } from 'lucide-react';
import { useAuth } from '@client/auth/useAuth';
import { formatZambiaPhone } from '@client/utils/validation';

interface Props {
  /** Called when the hamburger menu icon is pressed (future drawer). Optional. */
  onMenu?: () => void;
  /** Called when the bell icon is pressed (future notifications). Optional. */
  onBell?: () => void;
}

const BOX: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 12,
  background: '#ffffff',
  boxShadow: '0 1px 4px rgba(15,26,44,0.10)',
  border: '1px solid var(--c-border)',
  flexShrink: 0,
  cursor: 'pointer',
};

export default function AppHeader({ onMenu, onBell }: Props) {
  const { profile } = useAuth();

  const displayName = profile?.full_name
    ? profile.full_name
    : profile?.phone
    ? formatZambiaPhone(profile.phone)
    : '—';

  return (
    <header style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '12px 16px',
      background: 'var(--c-bg)',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    }}>
      {/* Left: hamburger */}
      <button
        type="button"
        aria-label="Open menu"
        style={BOX}
        onClick={onMenu}
      >
        <Menu size={18} color="var(--c-text)" strokeWidth={2} />
      </button>

      {/* Center: greeting + name */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 11, color: 'var(--c-text-muted)', fontWeight: 500, lineHeight: 1.2 }}>
          Mwabombeni
        </p>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--c-text)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </p>
      </div>

      {/* Right: bell with notification dot */}
      <div style={{ position: 'relative' }}>
        <button
          type="button"
          aria-label="Notifications"
          style={BOX}
          onClick={onBell}
        >
          <Bell size={18} color="var(--c-text)" strokeWidth={2} />
        </button>
        {/* Red notification dot */}
        <span style={{
          position: 'absolute',
          top: 6,
          right: 6,
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--c-danger)',
          border: '1.5px solid var(--c-bg)',
          pointerEvents: 'none',
        }} aria-hidden />
      </div>
    </header>
  );
}
